import type { UUID } from '@cmg/common';

import routeFactory from '../../common/util/routeFactory';
import type { NavItem } from '../offering-setup-next/components/OfferingSetupNav';

const getStepUrlPath =
  (stepId: string) =>
  ({ offeringId }: { offeringId: UUID }) => {
    return routeFactory.offeringSetup.getUrlPath({ offeringId, stepId });
  };

type NavItemsOptions =
  | { isPrivatePlacement: true }
  | {
      isPrivatePlacement: false;
      withOrderBookConfiguration: boolean;
      withUnderwritingTerms?: boolean;
    };

const privatePlacementNavItems = [
  {
    label: 'Issuer Details',
    stepId: 'info',
    getUrlPath: getStepUrlPath('info'),
  },
  {
    label: 'Offering Terms',
    stepId: 'private-terms',
    getUrlPath: getStepUrlPath('private-terms'),
  },
  {
    label: 'Documentation',
    stepId: 'documentation',
    getUrlPath: getStepUrlPath('documentation'),
  },
] satisfies NavItem[];

export const getNavItems = (options: NavItemsOptions): NavItem[] => {
  if (options.isPrivatePlacement) {
    return [...privatePlacementNavItems];
  }

  const { withOrderBookConfiguration, withUnderwritingTerms } = options;

  return [
    {
      label: 'Summary',
      stepId: 'summary',
      getUrlPath: getStepUrlPath('summary'),
    },
    {
      label: 'Issuer Details',
      stepId: 'info',
      getUrlPath: getStepUrlPath('info'),
    },
    {
      label: 'Instrument, Currency, Tranche',
      stepId: 'pricing',
      getUrlPath: getStepUrlPath('pricing'),
    },
    {
      label: 'Managers',
      stepId: 'managers',
      getUrlPath: getStepUrlPath('managers'),
    },
    {
      label: 'Timing',
      stepId: 'timing',
      getUrlPath: getStepUrlPath('timing'),
    },
    {
      label: 'Marketing',
      stepId: 'marketing',
      getUrlPath: getStepUrlPath('marketing'),
    },
    {
      label: 'Offering Terms',
      stepId: 'offering-terms',
      getUrlPath: getStepUrlPath('offering-terms'),
    },
    withOrderBookConfiguration
      ? {
          label: 'Order Book Configuration',
          stepId: 'order-book-configuration',
          getUrlPath: getStepUrlPath('order-book-configuration'),
        }
      : undefined,
    withUnderwritingTerms
      ? {
          label: 'Underwriting Terms',
          stepId: 'underwriting-terms',
          getUrlPath: getStepUrlPath('underwriting-terms'),
        }
      : undefined,
    {
      label: 'Documentation',
      stepId: 'documentation',
      getUrlPath: getStepUrlPath('documentation'),
    },
    {
      label: 'Disclaimers',
      stepId: 'disclaimers',
      getUrlPath: getStepUrlPath('disclaimers'),
    },
  ].filter((navItem): navItem is NavItem => !!navItem);
};
